import { LitElement, html, css } from 'lit';
import '../organisms/crusher-table.js';

/**
 * CrusherInvoiceList
 *
 * Billing-page primitive, displays past invoices in a table with predefined
 * columns and consistent formatting. Composes `crusher-table` for pagination
 * + sort + a11y; layers invoice-specific column rendering (formatted dates,
 * currency-aware amounts, status badges, download links) on top.
 *
 * Designed for products using a billing provider (Polar, Stripe, Paddle) that
 * exposes invoices via API. Consumer fetches the invoice list and passes it
 * via the `invoices` property; this component handles presentation.
 *
 * Properties:
 *   - invoices: array of invoice objects:
 *                       { id, number?, date, amount, currency?, status,
 *                         download_url?, hosted_url? }
 *                      `amount` is in major units (e.g., 12.50 = $12.50);
 *                      pass `amount` in minor units only if you also pass
 *                      `amount-in-cents` boolean (rare).
 *   - currency: fallback currency code (default "USD"); per-invoice
 *                      `currency` field wins if set
 *   - locale: fallback locale for date + amount formatting
 *                      (default "en-US")
 *   - loading: boolean; shows a "Loading invoices…" placeholder
 *                      instead of the table
 *   - empty-message , shown when `invoices` is empty + not loading
 *                      (default "No invoices yet.")
 *   - amount-in-cents, boolean; if true, amounts are minor units and get
 *                       divided by 100 before formatting
 *   - page-size: pagination (default 10; pass 0 for no pagination)
 *
 * Slots:
 *   - empty, overrides the built-in empty-state markup when no invoices
 *
 * Events:
 *   - invoice-download, { invoice }, fires when the download/view link
 *                        is clicked. Default href behaviour (link follows)
 *                        is preserved; consumers can `preventDefault()`
 *                        to take over (e.g., to add tracking).
 *
 * A11y: inherits crusher-table's row+column a11y (role=table, caption etc).
 */
export class CrusherInvoiceList extends LitElement {
  static properties = {
    invoices:      { type: Array },
    currency:      { type: String, reflect: true },
    locale:        { type: String, reflect: true },
    loading:       { type: Boolean, reflect: true },
    emptyMessage:  { type: String, attribute: 'empty-message', reflect: true },
    amountInCents: { type: Boolean, attribute: 'amount-in-cents', reflect: true },
    pageSize:      { type: Number, attribute: 'page-size', reflect: true }
  };

  constructor() {
    super();
    this.invoices = [];
    this.currency = 'USD';
    this.locale = 'en-US';
    this.loading = false;
    this.emptyMessage = 'No invoices yet.';
    this.amountInCents = false;
    this.pageSize = 10;
  }

  static styles = css`
    :host {
      display: block;
    }

    .loading,
    .empty {
      padding: var(--crusher-spacing-6);
      text-align: center;
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-md);
      background: var(--crusher-surface);
    }
  `;
  /*
   * NOTE on rendered cell styling:
   *   crusher-table slots the DOM nodes returned by column.render() into
   *   ITS OWN shadow root. Class-based CSS defined here in this component's
   *   shadow does NOT cross that boundary, so the cell styling is applied
   *   via inline `style` attributes using CSS custom properties. Custom
   *   properties DO inherit across shadow boundaries, so the styling stays
   *   token-driven and theme-aware.
   */

  /* Map provider-agnostic invoice status → tone for the badge. Covers Polar,
     Stripe, Paddle conventions. Unknown statuses render as neutral. */
  _statusTone(status) {
    const s = (status || '').toLowerCase();
    if (['paid', 'succeeded', 'complete', 'completed'].includes(s)) return 'success';
    if (['open', 'pending', 'processing', 'requires_action'].includes(s)) return 'warning';
    if (['failed', 'past_due', 'uncollectible', 'voided', 'canceled', 'cancelled'].includes(s)) return 'danger';
    return 'neutral';
  }

  /* Inline-style strings for status badges. Use CSS custom properties so
     the styling stays theme-aware across shadow-DOM boundaries (the cells
     are rendered into crusher-table's shadow, not this component's). */
  _statusStyle(tone) {
    const base = [
      'display:inline-flex',
      'align-items:center',
      'gap:0.375rem',
      'padding:0.25rem 0.625rem',
      'border-radius:var(--crusher-radius-full)',
      'font-size:var(--crusher-font-size-xs)',
      'font-weight:var(--crusher-font-weight-semibold)',
      'line-height:1',
      'letter-spacing:0.01em',
      'text-transform:capitalize',
      'border:1px solid transparent'
    ];
    const tones = {
      success: [
        'background:color-mix(in srgb, var(--crusher-feedback-success) 16%, transparent)',
        'color:var(--crusher-feedback-success)',
        'border-color:color-mix(in srgb, var(--crusher-feedback-success) 30%, transparent)'
      ],
      warning: [
        'background:color-mix(in srgb, var(--crusher-feedback-warning) 16%, transparent)',
        'color:var(--crusher-feedback-warning)',
        'border-color:color-mix(in srgb, var(--crusher-feedback-warning) 30%, transparent)'
      ],
      danger: [
        'background:color-mix(in srgb, var(--crusher-feedback-danger) 16%, transparent)',
        'color:var(--crusher-feedback-danger)',
        'border-color:color-mix(in srgb, var(--crusher-feedback-danger) 30%, transparent)'
      ],
      neutral: [
        'background:color-mix(in srgb, var(--crusher-fg) 10%, transparent)',
        'color:var(--crusher-fg-muted)',
        'border-color:color-mix(in srgb, var(--crusher-border) 70%, transparent)'
      ]
    };
    return [...base, ...(tones[tone] || tones.neutral)].join(';');
  }

  _downloadStyle() {
    return [
      'display:inline-flex',
      'align-items:center',
      'gap:0.375rem',
      'padding:0.375rem 0.75rem',
      'border-radius:var(--crusher-radius-md)',
      'border:1px solid var(--crusher-border)',
      'background:color-mix(in srgb, var(--crusher-fg) 4%, transparent)',
      'color:var(--crusher-fg)',
      'text-decoration:none',
      'font-size:var(--crusher-font-size-xs)',
      'font-weight:var(--crusher-font-weight-medium)',
      'line-height:1',
      'transition:background var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard), border-color var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard)',
      'cursor:pointer'
    ].join(';');
  }

  /* Construct a download / view icon as an SVG element built via DOM APIs
     (createElementNS), no innerHTML. Inherits color via currentColor so
     the styled link's `color` property drives the stroke. */
  _buildDownloadIcon(kind) {
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '12');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    const append = (tag, attrs) => {
      const el = document.createElementNS(NS, tag);
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      svg.appendChild(el);
    };
    if (kind === 'view') {
      append('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' });
      append('circle', { cx: '12', cy: '12', r: '3' });
    } else {
      append('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' });
      append('polyline', { points: '7 10 12 15 17 10' });
      append('line', { x1: '12', y1: '15', x2: '12', y2: '3' });
    }
    return svg;
  }

  _formatDate(value) {
    if (!value) return '-';
    /* Accept ISO strings, Date objects, or millisecond timestamps. */
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(this.locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  _formatAmount(amount, currency) {
    if (amount == null) return '-';
    const value = this.amountInCents ? amount / 100 : amount;
    const cur = currency || this.currency;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: cur
      }).format(value);
    } catch {
      /* Fall back to plain rendering if the currency code is unknown */
      return `${cur} ${value.toFixed(2)}`;
    }
  }

  /* Build the column descriptors for the wrapped crusher-table. The cell
     renderers return DOM nodes per crusher-table's render-hook contract. */
  _buildColumns() {
    const self = this;
    return [
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        render: (row) => {
          const span = document.createElement('span');
          span.textContent = self._formatDate(row.date);
          return span;
        }
      },
      {
        key: 'number',
        label: 'Invoice',
        render: (row) => {
          const span = document.createElement('span');
          span.textContent = row.number ?? row.id ?? '-';
          return span;
        }
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        render: (row) => {
          const span = document.createElement('span');
          span.textContent = self._formatAmount(row.amount, row.currency);
          return span;
        }
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => {
          const span = document.createElement('span');
          const tone = self._statusTone(row.status);
          /* Class + data-tone are the documented contract: consumers (and
             the framework's own browser-contract test) read them to verify
             the badge tone. Inline style is the visual rendering path,
             since class-based shadow CSS does not cross into the wrapped
             crusher-table's shadow root. */
          span.className = 'invoice-status';
          span.dataset.tone = tone;
          span.setAttribute('style', self._statusStyle(tone));
          /* Friendlier label for the most common provider-y status values
             (underscores swapped for spaces; capitalize via inline style). */
          const raw = row.status == null ? 'unknown' : String(row.status);
          span.textContent = raw.replace(/_/g, ' ');
          return span;
        }
      },
      {
        key: 'download',
        label: 'Invoice',
        align: 'end',
        render: (row) => {
          const url = row.download_url || row.hosted_url;
          if (!url) {
            const span = document.createElement('span');
            span.style.color = 'var(--crusher-fg-muted)';
            span.textContent = '-';
            return span;
          }
          const a = document.createElement('a');
          a.className = 'invoice-download';
          a.setAttribute('style', self._downloadStyle());
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          const kind = row.download_url ? 'download' : 'view';
          /* Build the icon SVG via DOM, not innerHTML, so the security
             hook stays clean. Icons are static literals, but we still
             prefer DOM construction for consistency with the rest of
             this component. */
          const iconWrap = document.createElement('span');
          iconWrap.style.display = 'inline-flex';
          iconWrap.setAttribute('aria-hidden', 'true');
          iconWrap.appendChild(self._buildDownloadIcon(kind));
          const label = document.createElement('span');
          label.textContent = kind === 'download' ? 'Download' : 'View';
          a.appendChild(iconWrap);
          a.appendChild(label);
          a.setAttribute('aria-label', kind === 'download' ? 'Download invoice' : 'View invoice');
          a.addEventListener('mouseenter', () => {
            a.style.background = 'color-mix(in srgb, var(--crusher-fg) 8%, transparent)';
            a.style.borderColor = 'color-mix(in srgb, var(--crusher-fg) 25%, transparent)';
          });
          a.addEventListener('mouseleave', () => {
            a.style.background = 'color-mix(in srgb, var(--crusher-fg) 4%, transparent)';
            a.style.borderColor = 'var(--crusher-border)';
          });
          a.addEventListener('click', () => {
            self.dispatchEvent(new CustomEvent('invoice-download', {
              detail: { invoice: row },
              bubbles: true,
              composed: true
            }));
          });
          return a;
        }
      }
    ];
  }

  /* Provide a stable per-row key for crusher-table (re-render efficiency
     + sort stability). Falls back to index if no id/number present. */
  _rowsWithKeys() {
    return (this.invoices || []).map((inv, i) => ({
      ...inv,
      key: inv.id ?? inv.number ?? `idx-${i}`
    }));
  }

  render() {
    if (this.loading) {
      return html`<div class="loading" role="status" aria-live="polite">Loading invoices…</div>`;
    }
    if (!this.invoices || this.invoices.length === 0) {
      const slot = html`<slot name="empty"><div class="empty">${this.emptyMessage}</div></slot>`;
      return slot;
    }

    const cols = this._buildColumns();
    const rows = this._rowsWithKeys();

    return html`
      <crusher-table
        .columns=${cols}
        .rows=${rows}
        .pageSize=${this.pageSize}
      ></crusher-table>
    `;
  }
}

customElements.define('crusher-invoice-list', CrusherInvoiceList);
